import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { 
  FileText, 
  Clock, 
  CheckCircle2,
  Eye,
  PenLine,
  Loader2,
  Bell,
  Edit3
} from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

var STATUS_LABELS = {
  incoming: 'Submitted',
  under_review: 'Under Review',
  needs_revision: 'Needs Revision',
  approved: 'Approved',
  published: 'Published',
  used: 'Used',
  rejected: 'Rejected'
};

var STATUS_COLORS = {
  incoming: 'bg-blue-100 text-blue-800',
  under_review: 'bg-yellow-100 text-yellow-800',
  needs_revision: 'bg-orange-100 text-orange-800',
  approved: 'bg-green-100 text-green-800',
  published: 'bg-emerald-100 text-emerald-800',
  used: 'bg-purple-100 text-purple-800',
  rejected: 'bg-red-100 text-red-800'
};

export default function ContributorDashboard() {
  var navigate = useNavigate();
  var auth = useAuth();
  var [dashboard, setDashboard] = useState(null);
  var [stories, setStories] = useState([]);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    if (!auth.isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchDashboard();
    fetchStories();
  }, [auth.isAuthenticated]);

  function fetchDashboard() {
    fetch(API_URL + '/api/contributor/dashboard', {
      headers: { 'Authorization': 'Bearer ' + auth.token }
    }).then(function(r) { return r.json(); })
      .then(function(d) { setDashboard(d); })
      .catch(function(e) { console.error(e); });
  }

  function fetchStories() {
    fetch(API_URL + '/api/contributor/my-stories', {
      headers: { 'Authorization': 'Bearer ' + auth.token }
    }).then(function(r) { return r.json(); })
      .then(function(d) { setStories(d); setLoading(false); })
      .catch(function(e) { console.error(e); setLoading(false); });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#07743F]" data-testid="contributor-loading" />
      </div>
    );
  }

  var userName = auth.user ? auth.user.name : 'Contributor';
  var byStatus = dashboard ? (dashboard.stories_by_status || {}) : {};
  var totalStories = dashboard ? (dashboard.total_stories || 0) : 0;
  var pendingCount = (byStatus.incoming || 0) + (byStatus.under_review || 0);
  var publishedCount = (byStatus.published || 0) + (byStatus.used || 0);
  var totalViews = 0;
  for (var i = 0; i < stories.length; i++) { totalViews += (stories[i].views || 0); }
  var storyCount = stories.length;
  var notifs = dashboard ? (dashboard.recent_notifications || []) : [];

  return (
    <div className="min-h-screen bg-gray-100" data-testid="contributor-dashboard">
      <div className="bg-gradient-to-r from-[#07743F] to-[#065a32] text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold" data-testid="contributor-welcome">Welcome, {userName}</h1>
              <p className="text-green-200 mt-1">Your Contributor Dashboard</p>
            </div>
            <Button onClick={function() { navigate('/submit'); }} className="bg-white text-[#07743F] hover:bg-green-50" data-testid="contributor-submit-btn">
              <PenLine className="w-4 h-4 mr-2" />
              Share New Story
            </Button>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-4 py-8">
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card><CardContent className="p-6"><div className="flex items-center gap-4"><div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center"><FileText className="w-6 h-6 text-blue-600" /></div><div><p className="text-2xl font-bold text-gray-900" data-testid="stat-total">{totalStories}</p><p className="text-sm text-gray-600">Total Stories</p></div></div></CardContent></Card>
          <Card><CardContent className="p-6"><div className="flex items-center gap-4"><div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center"><Clock className="w-6 h-6 text-yellow-600" /></div><div><p className="text-2xl font-bold text-gray-900" data-testid="stat-pending">{pendingCount}</p><p className="text-sm text-gray-600">Pending Review</p></div></div></CardContent></Card>
          <Card><CardContent className="p-6"><div className="flex items-center gap-4"><div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center"><CheckCircle2 className="w-6 h-6 text-green-600" /></div><div><p className="text-2xl font-bold text-gray-900" data-testid="stat-published">{publishedCount}</p><p className="text-sm text-gray-600">Published</p></div></div></CardContent></Card>
          <Card><CardContent className="p-6"><div className="flex items-center gap-4"><div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center"><Eye className="w-6 h-6 text-purple-600" /></div><div><p className="text-2xl font-bold text-gray-900" data-testid="stat-views">{totalViews}</p><p className="text-sm text-gray-600">Total Views</p></div></div></CardContent></Card>
        </div>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader><div className="flex items-center justify-between"><CardTitle>My Stories</CardTitle><Badge variant="secondary">{storyCount} total</Badge></div></CardHeader>
              <CardContent>
                {storyCount === 0 ? (
                  <div className="text-center py-12" data-testid="no-stories">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No stories yet</h3>
                    <p className="text-gray-600 mb-6">Share your first story!</p>
                    <Button onClick={function() { navigate('/submit'); }} className="bg-[#07743F] hover:bg-[#065a32] text-white"><PenLine className="w-4 h-4 mr-2" />Share Your First Story</Button>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="stories-list">
                    {stories.map(function(story) {
                      var statusLabel = STATUS_LABELS[story.status] || story.status;
                      var statusColor = STATUS_COLORS[story.status] || 'bg-gray-100 text-gray-800';
                      var canEdit = story.status === 'incoming' || story.status === 'needs_revision';
                      return (
                        <Card key={story.id} className="hover:shadow-md transition-shadow" data-testid={'contributor-story-' + story.id}>
                          <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <h3 className="font-semibold text-gray-900 line-clamp-1">{story.title}</h3>
                                  <Badge className={statusColor}>{statusLabel}</Badge>
                                </div>
                                <p className="text-sm text-gray-600 line-clamp-2 mt-1">{story.caption}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                              {canEdit ? <Button variant="outline" size="sm" className="border-[#07743F] text-[#07743F]" onClick={function() { navigate('/submit?edit=' + story.id); }} data-testid={'edit-story-' + story.id}><Edit3 className="w-3 h-3 mr-1" />Edit</Button> : null}
                              <Button variant="ghost" size="sm" className="text-gray-600" onClick={function() { navigate('/story/' + story.id); }} data-testid={'view-story-' + story.id}><Eye className="w-3 h-3 mr-1" />View</Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          <div className="space-y-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Story Status Guide</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Badge className="bg-blue-100 text-blue-800">Submitted</Badge>
                  <Badge className="bg-yellow-100 text-yellow-800 block w-fit">Under Review</Badge>
                  <Badge className="bg-green-100 text-green-800 block w-fit">Approved</Badge>
                  <Badge className="bg-emerald-100 text-emerald-800 block w-fit">Published</Badge>
                  <Badge className="bg-red-100 text-red-800 block w-fit">Rejected</Badge>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Bell className="w-4 h-4" />Recent Updates</CardTitle></CardHeader>
              <CardContent>
                {notifs.length > 0 ? (
                  <div className="space-y-3" data-testid="notifications-list">
                    {notifs.map(function(n, i) {
                      return (
                        <div key={i} className="text-sm border-l-2 border-[#72BF44] pl-3">
                          <p className="font-medium text-gray-900">{n.subject}</p>
                        </div>
                      );
                    })}
                  </div>
                ) : <p className="text-sm text-gray-500">No recent updates</p>}
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-[#07743F]/5 to-[#72BF44]/5">
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Story Tips</h3>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>- Be specific about challenges and solutions</li>
                  <li>- Include photos that show your work</li>
                  <li>- Share numbers and results when possible</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
